import React, { useEffect, useMemo, useState } from 'react';
import { renderShareCard } from '../utils/shareCard';
import { buildShareCaption, captionWithHashtags } from '../utils/shareCaptions';
import { rivalryShareUrl } from '../utils/seo';
import SnapshotModal from './SnapshotModal';
import './RivalryShareModal.css';

export interface RivalryTeamOption {
    team_id: number;
    team_name: string;
    color: string;
    share: number;
    counties: number;
    logo_url?: string | null;
    espn_team_id?: number | string | null;
}

interface RivalryShareModalProps {
    leagueId: string;
    leagueName: string;
    teams: RivalryTeamOption[];
    onClose: () => void;
}

function logoSrc(leagueId: string, team: RivalryTeamOption): string | undefined {
    if (team.logo_url) return team.logo_url;
    if (team.espn_team_id == null) return undefined;
    const id = Math.abs(Number(team.espn_team_id));
    if (leagueId.startsWith('ncaa_')) return `/logos/ncaa/${id}.png`;
    if (['nba', 'nfl', 'mlb', 'nhl', 'mls'].includes(leagueId)) return `/logos/${leagueId}/${id}.png`;
    return `/logos/${id}.png`;
}

const RivalryShareModal: React.FC<RivalryShareModalProps> = ({
    leagueId,
    leagueName,
    teams,
    onClose,
}) => {
    const alive = useMemo(() => teams.filter((t) => t.counties > 0), [teams]);
    const [teamAId, setTeamAId] = useState<number>(() => alive[0]?.team_id ?? teams[0]?.team_id ?? 0);
    const [teamBId, setTeamBId] = useState<number>(() => alive[1]?.team_id ?? teams[1]?.team_id ?? 0);
    const [shareBlob, setShareBlob] = useState<Blob | null>(null);
    const [building, setBuilding] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const teamA = teams.find((t) => t.team_id === teamAId) || alive[0];
    const teamB = teams.find((t) => t.team_id === teamBId) || alive[1];

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    const buildCard = async () => {
        if (!teamA || !teamB || teamA.team_id === teamB.team_id) {
            setError('Pick two different teams.');
            return;
        }
        setBuilding(true);
        setError(null);
        try {
            const pctA = Math.round(teamA.share * 100);
            const pctB = Math.round(teamB.share * 100);
            const leader = pctA >= pctB ? teamA : teamB;
            const blob = await renderShareCard({
                headline: `${teamA.team_name} vs ${teamB.team_name}`,
                subhead: `${pctA}% vs ${pctB}% of the ${leagueName} map`,
                accentColor: leader.color || '#f97316',
                badge: 'RIVALRY',
                teamLogoUrl: logoSrc(leagueId, leader),
            });
            setShareBlob(blob);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Could not build card');
        } finally {
            setBuilding(false);
        }
    };

    const shareCaption = teamA && teamB
        ? buildShareCaption({
            hook: 'rivalry',
            leagueName,
            teamName: teamA.team_name,
            rivalName: teamB.team_name,
            landShare: teamA.share,
        })
        : null;

    return (
        <>
            <div className="empire-modal-overlay rivalry-share-overlay" onClick={onClose} role="presentation">
                <div
                    className="empire-modal empire-modal--sm"
                    onClick={(e) => e.stopPropagation()}
                    role="dialog"
                    aria-labelledby="rivalry-share-title"
                >
                    <div className="empire-modal-head">
                        <h2 id="rivalry-share-title">Rivalry share card</h2>
                        <button type="button" className="empire-modal-close" onClick={onClose} aria-label="Close">✕</button>
                    </div>
                    <p className="rivalry-share-desc">Compare two empires and share a poster with land percentages.</p>

                    <div className="rivalry-share-pickers">
                        <label>
                            <span>Team A</span>
                            <select value={teamAId} onChange={(e) => setTeamAId(Number(e.target.value))}>
                                {teams.map((t, i) => (
                                    <option key={t.team_id} value={t.team_id}>
                                        #{i + 1} {t.team_name} ({Math.round(t.share * 100)}%)
                                    </option>
                                ))}
                            </select>
                        </label>
                        <label>
                            <span>Team B</span>
                            <select value={teamBId} onChange={(e) => setTeamBId(Number(e.target.value))}>
                                {teams.map((t, i) => (
                                    <option key={t.team_id} value={t.team_id}>
                                        #{i + 1} {t.team_name} ({Math.round(t.share * 100)}%)
                                    </option>
                                ))}
                            </select>
                        </label>
                    </div>

                    {error && <p className="rivalry-share-error">{error}</p>}

                    <div className="rivalry-share-actions">
                        <button type="button" className="empire-btn empire-btn--solid empire-btn--block" onClick={buildCard} disabled={building}>
                            {building ? 'Building…' : 'Generate card'}
                        </button>
                    </div>
                </div>
            </div>

            {shareBlob && shareCaption && teamA && (
                <SnapshotModal
                    blob={shareBlob}
                    fileName={`empire-maps-rivalry-${leagueId}.png`}
                    shareTitle={shareCaption.title}
                    shareText={captionWithHashtags(shareCaption)}
                    shareUrl={teamB
                        ? rivalryShareUrl(leagueId, teamA.team_id, teamB.team_id)
                        : `${window.location.origin}/league/${leagueId}`}
                    onClose={() => setShareBlob(null)}
                />
            )}
        </>
    );
};

export default RivalryShareModal;
